import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';

/**
 * Renders untrusted (already server-sanitised) HTML inside a sandboxed iframe with no script execution.
 * Uses a light "paper" surface in both themes so sender-styled emails stay readable.
 */
export function HtmlFrame({ html, title, className, minHeight = 60, maxHeight }: { html: string; title: string; className?: string; minHeight?: number; maxHeight?: number }) {
  const ref = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState(minHeight);
  const doc = `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="default-src 'none'; img-src data: blob: https: http: cid:; style-src 'unsafe-inline'; font-src data: https:;"><base target="_blank"><style>
    html,body{margin:0;padding:0;background:#fff;color:#1a2233;}
    body{font-family:Poppins,system-ui,-apple-system,'Segoe UI',sans-serif;font-size:15px;line-height:1.65;padding:16px 18px;word-wrap:break-word;overflow-wrap:anywhere;}
    img{max-width:100%;height:auto;} table{max-width:100%;} a{color:#1458c4;}
    blockquote{margin:0 0 0 .5em;padding-left:.8em;border-left:3px solid #d5dce8;color:#4b5567;}
    pre{white-space:pre-wrap;}
  </style></head><body>${html}</body></html>`;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    let ro: ResizeObserver | null = null;
    const measure = () => {
      try {
        const b = el.contentDocument?.body;
        if (!b) return;
        const h = Math.max(minHeight, b.scrollHeight + 4);
        setHeight(maxHeight ? Math.min(maxHeight, h) : h);
      } catch {
        /* cross-origin: keep default */
      }
    };
    const onLoad = () => {
      measure();
      try {
        const b = el.contentDocument?.body;
        if (b && 'ResizeObserver' in window) {
          ro = new ResizeObserver(measure);
          ro.observe(b);
        }
        el.contentDocument?.querySelectorAll('img').forEach((img) => img.addEventListener('load', measure));
      } catch {
        /* ignore */
      }
    };
    el.addEventListener('load', onLoad);
    return () => {
      el.removeEventListener('load', onLoad);
      ro?.disconnect();
    };
  }, [html, minHeight, maxHeight]);

  return (
    <iframe
      ref={ref}
      title={title}
      // allow-same-origin only so we can measure height; scripts are NOT allowed, so content cannot run code.
      sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
      srcDoc={doc}
      className={clsx('block w-full rounded-2xl border border-line bg-white', className)}
      style={{ height }}
    />
  );
}
